import { apiClient, apiClientAuth } from "./axios-config";

export const localeApi = {
  getStringResources: (locale) => {
    return apiClient.request({
      method: "GET",
      url: `/resources/category/resources/${locale}`,
    });
  },
  getAdditionalStringResources: (locale) => {
    return apiClient.request({
      method: "GET",
      url: `/resources/category/additional_services/${locale}`,
    });
  },
  getFAQResources: (locale) => {
    return apiClient.request({
      method: "GET",
      url: `/resources/category/faq/${locale}`,
      headers: { "X-Company-Name": "default" }, // faq is only linekd to default tenant
    });
  },
};

export const companyProfileApi = {
  getCompanyProfileData: (companyId) => {
    return apiClient.request({
      method: "GET",
      url: `/tenant/name/${companyId}`,
    });
  },
};

export const airportsApi = {
  getAirports: (query, locale) => {
    return apiClient.request({
      method: "GET",
      url: `/airport/search?locale=${locale}&${query}`,
    });
  },
  getCityDetails: (iata) => {
    return apiClient.request({
      method: "GET",
      url: `/airport/iata/${iata}`,
    });
  },
  getPopularDestinationAirports: (iata) => {
    let url = `/destination/source`;
    url += iata ? `/${iata}` : "";
    return apiClient.request({
      method: "GET",
      url: url,
    });
  },
  getPopularArrivalAirports: (iata) => {
    let url = `/destination/popular`;
    url += iata ? `/${iata}` : "";
    return apiClient.request({
      method: "GET",
      url: url,
    });
  },
};

export const resultsApi = {
  getSearchResultsKey: (query) => {
    return apiClient.request({
      method: "GET",
      url: `/search${query}`,
      timeout: 3000,
      timeoutErrorMessage: "Search request timed out (3000ms).",
    });
  },
  getSpecificFlight: (query, mergeKey, dataProvider) => {
    console.log(`/search?${query}&merge_key=${mergeKey}&data_provider=${dataProvider}`);
    return apiClient.request({
      method: "GET",
      url: `/search?${query}&merge_key=${mergeKey}&data_provider=${dataProvider}`,
    });
  },
};

export const additionalServicesApi = {
  getServices: () => {
    return apiClient.request({
      method: "GET",
      url: `/services/all`,
    });
  },
};

export const ancillaryServicesApi = {
  getServices: (carrierCode) => {
    return apiClient.request({
      method: "GET",
      url: `/luggage/code/${carrierCode}`,
    });
  },
};

export const bookingOrderApi = {
  startBooking: (travelPackage) => {
    return apiClient.request({
      method: "POST",
      data: travelPackage,
      url: `/booking/create`,
    });
  },
  getOrderById: (orderId) => {
    return apiClientAuth.request({
      method: "GET",
      url: `/booking/retrieve/${orderId}`,
    });
  },
  getUserTrips: (userId) => {
    return apiClientAuth.request({
      method: "GET",
      url: `/booking/user/${userId}`,
    });
  },
  sendGroupBookingRequest: (groupType, data) => {
    return apiClient.request({
      method: "POST",
      data: data,
      url: `/booking/addGroupBooking/${groupType}`,
    });
  },
  getOrderItems: (orderId) => {
    return apiClientAuth.request({
      method: "GET",
      url: `/booking/getBookingAndCommit/${orderId}`,
    });
  },
  batchUpdate: (updates, orderId, cancelToken) => {
    return apiClientAuth.request({
      method: "POST",
      data: updates,
      url: `/booking/batchUpdate/${orderId}`,
      cancelToken,
    });
  },
};

export const paymentApi = {
  getPaymentMethods: () => {
    return apiClient.request({
      method: "GET",
      url: "/payment/methods",
    });
  },
  processPaymentOrder: (payload) => {
    return apiClient.request({
      method: "POST",
      data: { payload },
      headers: {
        "Content-Type": "application/json",
      },
      url: "/payment/order",
    });
  },
  checkPaymentOrderToken: (token) => {
    return apiClient.request({
      method: "POST",
      url: "/payment/validate",
      data: { token },
    });
  },
};

export const staticApi = {
  getStaticBanners: (language) => {
    return apiClient.request({
      method: "GET",
      url: `/static/links/banners/default/${language}`,
    });
  },
  getStaticFiles: async (company, language, folderName) => {
    return apiClient
      .request({
        method: "GET",
        url: `/static/links/${folderName}/${company}/${language}`,
      })
      .then(async (res) => {
        if (!Array.isArray(res.data) || res.data.length === 0) {
          throw new Error(
            "Invalid data structure: expected an array with at least one element."
          );
        }

        const data = {};
        const promises = [];
        res.data.forEach(async (targetLink) => {
          if (!targetLink) throw new Error("No target link found.");

          const ext = targetLink.split(".").pop();
          if (ext === "css" || ext === "html") {
            const promise = apiClient
              .request({
                method: "GET",
                url: targetLink,
                headers: {
                  "Content-Type": `text/${ext}`,
                },
                responseType: "text",
              })
              .then((res) => {
                data[ext] = res.data;
              })
              .catch((err) => {
                console.log(err);
                throw err;
              });
            promises.push(promise);
          }
        });
        await Promise.all(promises);
        return data;
      })
      .catch((err) => {
        console.log(err);
        if (err.response && err.response.status === 404) {
          return { html: "", css: "" };
        } else {
          throw err;
        }
      });
  },
};

export const fileApi = {
  getInvoice: (bookingNumber) => {
    return apiClient.request({
      method: "GET",
      url: `/invoice/generate/${bookingNumber}`,
      headers: {
        "Content-Type": "application/pdf",
      },
      responseType: "blob",
    });
  },
  getUserFile: (url) => {
    return apiClientAuth.request({
      method: "GET",
      url,
      responseType: "blob",
    });
  },
};

export const ticketApi = {
  getDocuments(correlationId) {
    return apiClientAuth.request({
      method: "GET",
      url: `/ticket/otherDocument/booking/${correlationId}`,
    });
  },
  getEticketBinary(id) {
    return apiClientAuth.request({
        method: "GET",
        url: `/ticket/otherDocument/getFileBinary/${id}`,
        responseType: "blob",
    });
  },
};

export const destinationsApi = {
  getTravelDeals: () => {
    return apiClient.request({
      method: "GET",
      url: "/destination/retrieve_offers",
    });
  },
};

export const statisticsApi = {
  getCheapFlights: (type, s_iata, d_iata, min, max, start, end, summary) => {
    return apiClient.request({
      method: "GET",
      url: "/statistics/cheap-flights",
      params: { type, s_iata, d_iata, min, max, start, end, summary },
    });
  },
};

export const promoApi = {
  validateCode: (code) => {
    return apiClient.request({
      method: "GET",
      url: `/promo/validation/${code}`,
    });
  },
};

export const calendarApi = {
  getDirectFlightDates: (origin, destination) => {
    return apiClient.request({
      method: "GET",
      url: `/calendar/${origin}/${destination}`,
    });
  },
};
